/**
 * Every tunable behaviour value of the CHARGlink BRM public ranking page (ST-2695 / ST-2696). Changing a
 * value here takes effect without any other code change: the component reads the timings and counts, the
 * animations read their duration / easing / type, and the max batteries and pulse timings reach the
 * stylesheet as CSS custom properties bound on the page root. Sizes and colors live in the stylesheet.
 */

export type PageTransitionType = 'slide' | 'fade' | 'none';

export interface GroupsPerPageBreakpoint {
	/** Smallest width available to the page (px) this entry applies to. */
	minWidth: number;
	groupsPerPage: number;
}

export interface BrmPublicPageConfig {
	paging: {
		/** Checked in order; the first entry whose `minWidth` fits the available width wins. */
		breakpoints: readonly GroupsPerPageBreakpoint[];
		/** How long each page stays on screen before rotating to the next one. */
		pageTimeMs: number;
	};
	batteries: {
		/** Ready to Pick boxes a card shows and sizes for; extra batteries are not rendered. */
		maxPerGroup: number;
		/** The decorative battery image sits at the card's bottom while the group has this many Ready to Pick batteries or fewer. */
		deviceImageBelowCount: number;
	};
	/**
	 * Longest text (characters) shown at full font size. Longer battery IDs and group names shrink
	 * in proportion, so they always fit their box or header instead of being clipped.
	 */
	textFit: { locationFullSizeMaxChars: number; groupNameFullSizeMaxChars: number };
	animation: {
		pageTransition: { type: PageTransitionType; durationMs: number; easing: string };
		boxEnterLeave: { durationMs: number; easing: string; offsetPx: number };
		/** The swap between a group's ready list and its "No … Batteries Available" message. */
		emptySwap: { durationMs: number; easing: string; offsetPx: number; enterDelayMs: number };
		newBatteryPulse: { count: number; durationMs: number };
		alertPulse: { durationMs: number };
	};
	data: {
		pollIntervalMs: number;
		pageReloadMs: number;
		/** Consecutive failed polls after which the page stops its auto-reload timer. */
		offlineFailureThreshold: number;
	};
	buzzer: {
		/** How long the sound keeps going after the last poll that still had an alert (a fail-safe if polling stops). */
		playTimeMs: number;
		src: string;
		/** Per-site localStorage key prefix for the Enable/Disable Buzzer choice; the site ID is appended. */
		muteStorageKeyPrefix: string;
	};
}

export const BRM_PUBLIC_PAGE_CONFIG: Readonly<BrmPublicPageConfig> = Object.freeze<BrmPublicPageConfig>({
	paging: {
		breakpoints: [
			{ minWidth: 1024, groupsPerPage: 3 },
			{ minWidth: 600, groupsPerPage: 2 },
			{ minWidth: 0, groupsPerPage: 1 },
		],
		pageTimeMs: 7000,
	},
	batteries: {
		maxPerGroup: 4,
		deviceImageBelowCount: 3,
	},
	textFit: {
		locationFullSizeMaxChars: 10,
		groupNameFullSizeMaxChars: 20,
	},
	animation: {
		pageTransition: { type: 'slide', durationMs: 700, easing: 'ease-in-out' },
		boxEnterLeave: { durationMs: 700, easing: 'ease-in-out', offsetPx: 40 },
		emptySwap: { durationMs: 700, easing: 'ease-in-out', offsetPx: 100, enterDelayMs: 700 },
		newBatteryPulse: { count: 3, durationMs: 1000 },
		alertPulse: { durationMs: 1000 },
	},
	data: {
		pollIntervalMs: 3000,
		pageReloadMs: 15 * 60 * 1000,
		offlineFailureThreshold: 30,
	},
	buzzer: {
		playTimeMs: 16000,
		src: '/sctportal/audio/buzz.mp3',
		muteStorageKeyPrefix: 'brm_public_buzzer_enabled_',
	},
});

/**
 * Font scale (0–1) for a text: full size up to `fullSizeMaxChars`, then shrinking with the square root
 * of the overflow — the text also wraps, so it gains lines as well as losing size and stays readable.
 */
export function textFitScale(text: string, fullSizeMaxChars: number): number {
	const length = (text || '').length;
	return length > fullSizeMaxChars ? Math.sqrt(fullSizeMaxChars / length) : 1;
}

/** Maps the available width to groups per page using the configured breakpoints. */
export function groupsPerPageForWidth(width: number, breakpoints = BRM_PUBLIC_PAGE_CONFIG.paging.breakpoints): number {
	const match = breakpoints.find((breakpoint) => width >= breakpoint.minWidth);
	return match ? match.groupsPerPage : breakpoints[breakpoints.length - 1].groupsPerPage;
}

/**
 * Start index of every page. Pages step by `perPage`; the last start is clamped to
 * `groupCount - perPage` so the last page is always full, filled with the groups just before it
 * (5 groups, 3 per page → [0, 2]). Everything that fits on one page is a single page.
 */
export function buildPageStarts(groupCount: number, perPage: number): number[] {
	if (perPage <= 0 || groupCount <= perPage)
		return [0];

	const starts: number[] = [];
	for (let start = 0; start < groupCount; start += perPage)
		starts.push(Math.min(start, groupCount - perPage));

	return starts;
}
